import React, { useEffect, useState, useRef } from "react";
import { getAllTeacherAnalyses } from "../../../services/operations/teacherAnalysisAPI";
import { useTranslation } from "react-i18next";

const LearningPathDesigner = () => {
  const { t } = useTranslation();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef(null); // reference to dropdown wrapper

  useEffect(() => {
    const fetchAnalyses = async () => {
      setLoading(true);
      const data = await getAllTeacherAnalyses()();
      if (data) {
        setAnalyses(data.data || []);
        setFiltered(data.data || []);
      }
      setLoading(false);
    };
    fetchAnalyses();
  }, []);

  useEffect(() => {
    if (search.trim() === "") {
      setFiltered(analyses);
      setShowDropdown(false);
    } else {
      const filteredData = analyses.filter((item) =>
        item.teacherName.toLowerCase().includes(search.toLowerCase())
      );
      setFiltered(filteredData);
      setShowDropdown(true);
    }
  }, [search, analyses]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectTeacher = (teacherName) => {
    setSearch(teacherName);
    setShowDropdown(false);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 bg-gray-50 min-h-screen space-y-8">
      <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm space-y-6">
        {/* Page Heading */}
        <h2 className="text-2xl font-bold text-gray-800">{t("learningPath.title")}</h2>

        {/* Search Bar */}
        <div className="relative w-full max-w-sm" ref={dropdownRef}>
          <input
            type="text"
            placeholder={t("learningPath.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {showDropdown && filtered.length > 0 && (
            <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
              {filtered.map((item) => (
                <li
                  key={item._id}
                  className="p-2 hover:bg-indigo-100 cursor-pointer"
                  onClick={() => handleSelectTeacher(item.teacherName)}
                >
                  {item.teacherName}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Teacher Analyses */}
        <div className="w-full bg-gray-50 p-4 rounded-2xl border space-y-4 mt-4">
          {loading ? (
            <div className="text-gray-500">{t("learningPath.loading")}</div>
          ) : filtered.length === 0 ? (
            <div className="text-gray-400">{t("learningPath.noAnalysesFound")}</div>
          ) : (
            filtered.map((analysis) => (
              <div
                key={analysis._id}
                className="bg-white p-4 rounded-md border shadow-sm space-y-2"
              >
                <h4 className="font-semibold text-gray-700">{analysis.teacherName}</h4>
                <p className="text-gray-600 text-sm">
                  <strong>{t("learningPath.summary")}:</strong> {analysis.aiAnalysis?.overallSummary}
                </p>

                <div>
                  <strong>{t("learningPath.strengths")}:</strong>
                  <ul className="list-disc list-inside text-gray-600 text-sm">
                    {analysis.aiAnalysis?.identifiedStrengths.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <strong>{t("learningPath.areasForImprovement")}:</strong>
                  <ul className="list-disc list-inside text-gray-600 text-sm">
                    {analysis.aiAnalysis?.areasForImprovement.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <strong>{t("learningPath.actionableSuggestions")}:</strong>
                  <ul className="list-disc list-inside text-gray-600 text-sm">
                    {analysis.aiAnalysis?.actionableSuggestions.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                <p className="text-gray-400 text-xs">
                  {t("learningPath.createdAt")} {new Date(analysis.aiAnalysis?.analysisCreatedAt).toLocaleString()}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningPathDesigner;
